import { useEffect, useState } from 'react'
import { sortBy, takeRight } from 'lodash'
import moment from 'moment'
import { useAnimationControls } from 'framer-motion'

export const parseMobileDate = (date) => moment(date).format('MMM Do')

const getInitialYear = (firstItemDate) => moment(firstItemDate?.date).year()

const _getDataControls = (dataSource) => {
  // Generate a list of years from YEAR_START to current year

  const startYear = dataSource.length > 0 ? getInitialYear(dataSource[0]) : 2010

  const dataYears = Array.from(
    { length: new Date().getFullYear() - startYear + 1 },
    (_, i) => startYear + i,
  )
  // Add extra year in order to finish as same as figma years list
  dataYears.push(String(Number(dataYears[dataYears.length - 1]) + 1))

  // Return the list of year events with the last five events per year, if a year has no event, then fill with blank values
  // generating five events per year
  const dataEvents = dataYears
    .map((year) => {
      const _events = dataSource.filter((x) => x.year === year)

      const events = takeRight(sortBy(_events, ['year']), 5)
      const isIncomplete = events.length < 5
      const difference = isIncomplete ? 5 - events.length : 0
      const totalEvents =
        dataYears.indexOf(year) === dataYears.length - 1
          ? events.concat(Array(1).fill({ id: null, date: '', year: year, title: '' }))
          : [...events].concat(
              Array(difference).fill({ id: null, date: '', year: year, title: '' }),
            )

      return {
        year: parseInt(year),
        events: totalEvents.map((x, i) => ({
          id: x.id,
          date: x.date,
          year: x.year,
          eventTitle: x?.title ?? '',
          primary: i === 0,
          page: x?.page ?? '',
        })),
      }
    })
    .map((event) => event.events)
    .flat()
  return dataEvents
}

export default function useTimeline(story_events) {
  const timelineControls = useAnimationControls()
  const [timelineCurrentEvent, setTimelineCurrentEvent] = useState({
    event: {
      id: 0,
      title: 'New Event',
      date: new Date(),
      event_picture: { url: '' },
      description: '',
      page: '',
    },
    isOlder: false,
  })
  const [timeLineEvents, setTimeLineEvents] = useState([])

  const getEvent = (event, current) => {
    // debugger
    const isValidCurrent = current !== null && current !== event
    if (!event) {
      return {
        event: story_events[0],
        isOlder: false,
      }
    }
    if (!isValidCurrent) {
      return {
        event: story_events.find((x) => x.id === event),
        isOlder: false,
      }
    }
    const currentEvent = current ? story_events.find((x) => x.id === current) : story_events[0]
    const incommingEvent = event ? story_events.find((x) => x.id === event) : story_events[0]
    return {
      event: incommingEvent,
      isOlder: new Date(incommingEvent.date).getTime() > new Date(currentEvent.date).getTime(),
    }
  }

  // action which captures the event after the current one.
  const selectNext = (current) => {
    const currentEventIndex = story_events.findIndex((x) => x.id === current.id)
    const len = story_events.length
    const next = currentEventIndex + 1
    return {
      event: next < len ? story_events[currentEventIndex + 1] : story_events[currentEventIndex],
      isOlder: false,
    }
  }
  // action which captures the event before the current one.
  const selectPrevious = (current) => {
    const currentEventIndex = story_events.findIndex((x) => x.id === current.id)
    return {
      event: currentEventIndex > 0 ? story_events[currentEventIndex - 1] : story_events[0],
      isOlder: true,
    }
  }

  const previous = async () => {
    await timelineControls.start({
      x: 20,
      opacity: 0,
      transition: {
        duration: 0.5,
      },
    })
    timelineControls.set({ opacity: 0, x: -20 })
    // previous
    const newEvent = selectPrevious(timelineCurrentEvent.event)
    setTimelineCurrentEvent(newEvent)
    await timelineControls.start({
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
      },
    })
  }
  const next = async () => {
    await timelineControls.start({
      x: -20,
      opacity: 0,
      transition: {
        duration: 0.5,
      },
    })
    timelineControls.set({ opacity: 0, x: 20 })
    // next
    const newEvent = selectNext(timelineCurrentEvent.event)
    setTimelineCurrentEvent(newEvent)
    await timelineControls.start({
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
      },
    })
  }

  // Output actions
  const selectEvent = async (event, current) => {
    if (event !== current) {
      const newEvent = getEvent(event, current)
      if (newEvent.isOlder) {
        await timelineControls.start({
          x: -20,
          opacity: 0,
          transition: {
            duration: 0.5,
          },
        })
        timelineControls.set({ opacity: 0, x: 20 })
        setTimelineCurrentEvent(newEvent)
        await timelineControls.start({
          opacity: 1,
          x: 0,
          transition: {
            duration: 0.5,
          },
        })
      } else {
        await timelineControls.start({
          x: 20,
          opacity: 0,
          transition: {
            duration: 0.5,
          },
        })
        timelineControls.set({ opacity: 0, x: -20 })
        setTimelineCurrentEvent(newEvent)
        await timelineControls.start({
          opacity: 1,
          x: 0,
          transition: {
            duration: 0.5,
          },
        })
      }
    }
  }

  useEffect(() => {
    const getAllEvents = _getDataControls(story_events)
    setTimeLineEvents(getAllEvents)
    setTimelineCurrentEvent(getEvent())
  }, [])

  return {
    timelineCurrentEvent,
    selectEvent,
    timeLineEvents,
    next,
    previous,
    timelineControls,
  }
}
